import React, {forwardRef, PropsWithChildren, ReactElement, ReactNode, useEffect, useImperativeHandle, useRef, useState} from 'react';
import cn from "classnames";
import {usePopper} from 'react-popper';
import scrollIntoView from 'scroll-into-view-if-needed'

import './SearchDropdown.scss';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {clamp} from 'services/Utils';
import {Keys, AsyncStatus} from 'enums';
import {ISearcherOnSearch, parseAxiosError} from 'services';
import {useLoadMore, useSearcher} from 'hooks';
import {Loader} from 'components/Loader/Loader';

interface ISearchDropdownProps<IOption> {
    className?: string,
    disabled?: boolean,
    dontDebounce?: boolean,
    getOptionValue: (option: IOption) => any,
    hideClearButton?: boolean,
    inputRef?: React.MutableRefObject<HTMLInputElement>,
    onChange: (option: IOption) => void,
    onSearch: ISearcherOnSearch<IOption>,
    placeholder?: ReactNode,
    renderOption: (option: IOption) => ReactNode,
    style?: React.CSSProperties,
    value: IOption,
}

// Public API, exposed via a ref.
export interface ISearchDropdownAPI {
    focus: () => void,
    reset: () => void,
}

// We have separate states for opening/closing to make css animation work.
enum OpenState {
    Closed = 'Closed',
    Opening = 'Opening',
    Open = 'Open',
    Closing = 'Closing',
}

// Popper modifier to make the dropdown the same width as the input.
// From: https://popper.js.org/docs/v2/modifiers/community-modifiers/
const sameWidth = {
    name: "sameWidth",
    enabled: true,
    phase: "beforeWrite",
    requires: ["computeStyles"],
    fn: ({state}) => {
        state.styles.popper.width = `${state.rects.reference.width}px`;
    },
    effect: ({state}) => {
        state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
    }
};

// Generic search dropdown, expects to be passed an interface describing a search result.
function _SearchDropdown<IOption>(props: PropsWithChildren<ISearchDropdownProps<IOption>>, ref: React.MutableRefObject<ISearchDropdownAPI>) {
    let {
        className,
        disabled,
        dontDebounce,
        getOptionValue,
        hideClearButton,
        inputRef,
        onChange,
        onSearch,
        placeholder,
        renderOption,
        style,
        value,
    } = props;
    const [hoverResult, setHoverResult] = useState<IOption>();
    const [inputValue, setInputValue] = useState('');

    // If the user didn't provide an input ref, we'll create one.
    const localInputRef = useRef<HTMLInputElement>(null)
    if (!inputRef) inputRef = localInputRef;

    // Animation props
    const animDuration = 50; // In ms, this has to match the css.
    const animRAFRef = useRef<number>();
    const animTimeoutRef = useRef<NodeJS.Timeout>();
    const [openState, setOpenState] = useState(OpenState.Closed);
    const [isFocused, setIsFocused] = useState(false);

    // Popper props
    const [popReferenceEl, setPopReferenceEl] = useState(null);
    const [popEl, setPopEl] = useState(null);
    const pop = usePopper(popReferenceEl, popEl, {
        placement: 'bottom-start',
        modifiers: [
            {name: 'flip', enabled: false},
            sameWidth as any,
        ],
    });

    // We're rebinding this every render because handleDocClick needs the current state.
    // If we only do this once, it'll get stale state. This might have a performance impact.
    useEffect(() => {
        // listen for clicks and close dropdown on body
        document.addEventListener("mousedown", handleDocClick);
        return () => {
            document.removeEventListener("mousedown", handleDocClick);
        };
    });

    // Cancel any animation when we unmount
    useEffect(() => {
        return () => {
            cancelAnimation();
        }
    }, []);


    // -------------------- Searcher Wireup ---------------------
    // This is what loads the items from an API.
    const {
        canLoadMore,
        err,
        results,
        status: searcherStatus,
        loadMore,
        reset: searcherReset,
        search,
        searchDebounced
    } = useSearcher(onSearch);

    // To scroll to an option for keyboard nav, we need a ref for every result.
    const resultRefs = results.map(result => React.createRef<HTMLDivElement>());


    // -------------------- useLoadMore Wireup ---------------------
    // This makes the search load more items if the user scrolls down.
    const {scrollerRef} = useLoadMore(loadMore);


    // -------------------- Public API  ---------------------
    // A parent component can call these methods by using a ref like:
    //     <SearchDropdown ref={dropdownRef} />
    //     ...
    //     dropdownRef.current.focus();
    useImperativeHandle(ref, () => ({
        focus: () => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        },
        reset: () => {
            searcherReset();
            close();
        }
    }));


    // -------------------- Internal Control Methods ---------------------

    // To make CSS animation work, we're using 2 classes:
    //     "open" controls "display: block"
    //     "in" controls the animation properties like "transform" and "opacity"
    //
    // This is because browsers cancel animations if you make layout modifications,
    // like changing an element from "display: none" to "display: block". So, we have
    // change the element to block before we try to animate anything.
    //
    // Here's the mapping:
    //               open    in
    //     Closed:   false   false
    //     Opening:  true    false
    //     Open:     true    true
    //     Closing:  true    false

    function open() {
        if (disabled) return;
        if (openState === OpenState.Open || openState === OpenState.Opening) return;

        focusInput();

        // Search if we haven't yet.
        if ((searcherStatus === AsyncStatus.Initial || searcherStatus === AsyncStatus.Error) && results.length === 0) {
            search(inputValue);
        }

        switch (openState) {
            case OpenState.Closed:
                setOpenState(OpenState.Opening);
                // Now that we're open, need to tell popper to update.
                // It can't know the right position when we're closed.
                pop.update();

                // After we render, go to Open, which adds the "in" class to activate css animation
                animRAFRef.current = requestAnimationFrame(() => {
                    setOpenState(OpenState.Open);
                })
                break;
            case OpenState.Closing:
                cancelAnimation();
                setOpenState(OpenState.Open);
                break;
        }
    }

    function close() {
        if (openState === OpenState.Closed || openState === OpenState.Closing) return;

        // Since we're clearing the search text, if we had previously searched, we need to clear the results
        // so that we re-search when we open.
        if (inputValue !== '') {
            searcherReset();
        }
        setInputValue('');
        setHoverResult(undefined);
        focusInput();

        switch(openState) {
            case OpenState.Open:
                setOpenState(OpenState.Closing);

                // We need to wait for the CSS animation to finish before we hide the dropdown.
                animTimeoutRef.current = setTimeout(() => {
                    setOpenState(OpenState.Closed);
                }, animDuration);
                break;
            case OpenState.Opening:
                cancelAnimation();
                // Rather than try to figure out how much time is left in the animation,
                // we're just going to immediately close.
                setOpenState(OpenState.Closed);
                break;
        }
    }

    function cancelAnimation() {
        cancelAnimationFrame(animRAFRef.current);
        clearTimeout(animTimeoutRef.current);
    }

    function selectItem(o: IOption) {
        if (disabled) return;

        onChange(o);
        close();
    }

    function moveSelection(moveDiff: number) {
        if (disabled) return;
        if (results.length === 0) return;

        let newResultI = 0;

        if (hoverResult) {
            const hoverValue = getOptionValue(hoverResult);
            // We already have a value hovered, so find its current index.
            const curI = results.findIndex((r) => {
                return getOptionValue(r) === hoverValue;
            })

            if (curI !== -1) {
                // Move the index, then clamp to the array bounds
                newResultI = clamp(curI + moveDiff, 0, results.length - 1);
            }
        }

        setHoverResult(results[newResultI]);

        // Scroll to the selected item. We're using a helper lib to only scroll if needed.
        const ref = resultRefs[newResultI];
        if (ref && ref.current) {
            scrollIntoView(ref.current, {
                scrollMode: 'if-needed',
                block: (moveDiff > 0) ? 'end' : 'start',
                inline: 'start',
            })
        }
    }

    function focusInput() {
        inputRef.current && inputRef.current.focus();
    }

    // -------------------- Event Handlers ---------------------

    function onFocusInput() {
        if (!isFocused) {
            setIsFocused(true);
        }
    }

    function onBlurInput() {
        if (isFocused) {
            setIsFocused(false);
        }
    }

    function onButtonClick() {
        switch (openState) {
            case OpenState.Opening:
            case OpenState.Open:
                close();
                break;
            case OpenState.Closing:
            case OpenState.Closed:
                if (disabled) return;
                open();
        }
    }

    function onClear() {
        if (disabled) return;
        close();
        selectItem(undefined);
        setHoverResult(undefined);
        searcherReset();
        focusInput();
    }

    function onKeyDownInput(e: React.KeyboardEvent) {
        if (disabled) return;

        switch (e.key) {
            // Move selection
            case Keys.ArrowDown:
            case Keys.ArrowUp:
                e.preventDefault();
                if (openState !== OpenState.Open && openState !== OpenState.Opening) {
                    // Open if we're not already open
                    open();
                } else {
                    // Move the selection
                    let diff = 0;
                    if (e.key === Keys.ArrowDown) diff = 1;
                    else  diff = -1;

                    moveSelection(diff);
                }
                break;
            // Select item
            case Keys.Enter:
                if (openState !== OpenState.Open && openState !== OpenState.Opening) {
                    // Open if we're not already open
                    open();
                } else {
                    if (hoverResult) {
                        selectItem(hoverResult);
                    }
                }
                break;
            // Close
            case Keys.Escape:
                if (openState === OpenState.Open || openState === OpenState.Opening) {
                    e.preventDefault();
                    close();
                }
                break;
            // Close if open
            case Keys.Tab:
                if (openState === OpenState.Open || openState === OpenState.Opening) {
                    e.preventDefault();
                    close();
                }
                break;
            // Open on spacebar to act like a normal select
            case ' ':
                if (openState === OpenState.Closed || openState === OpenState.Closing) {
                    e.preventDefault();
                    open();
                }
                break;
        }
    }

    function onChangeInput(e: React.ChangeEvent<HTMLInputElement>) {
        if (disabled) return;

        const val = e.target.value
        setInputValue(val);
        setHoverResult(undefined);
        open();
        if (dontDebounce) {
            search(val);
        } else {
            searchDebounced(val);
        }
    }

    function onClickItemDisplay() {
        if (disabled) return;

        open();
    }

    // We're managing hover state via javascript so that it works for keyboard nav.
    function onEnterItem(o: IOption) {
        if (disabled) return;

        setHoverResult(o);
    }

    // Close when the user clicks outside the dropdown.
    function handleDocClick(event) {
        // Ignore clicks if we're not open.
        if (openState !== OpenState.Opening && openState !== OpenState.Open) return;

        // Ignore clicks from our element or from the dropdown.
        if (popReferenceEl && popReferenceEl.contains(event.target)) return;
        if (popEl && popEl.contains(event.target)) return;

        close();
    }

    const isOpen = openState !== OpenState.Closed;
    const isIn = openState === OpenState.Open;
    const selectedOptionValue = value && getOptionValue(value);

    return (<>
        <div
            className={cn(className, 'searchDropdown_wrap', {
                highlighted: isFocused || isOpen,
                open: isOpen,
                disabled: disabled,
            })}
            ref={setPopReferenceEl}
            style={style}
        >
            <div className="searchDropdown_inputArea" onClick={onClickItemDisplay}>
                <div className={cn("searchDropdown_itemDisplay", {
                    hidden: openState === OpenState.Opening || openState === OpenState.Open,
                })}>
                    {value && renderOption(value)}
                    {!value && placeholder && (
                        <span className="searchDropdown_placeholder">{placeholder}</span>
                    )}
                </div>
                <input
                    className={cn("searchDropdown_input", {
                        hidden: openState === OpenState.Closing || openState === OpenState.Closed,
                    })}
                    type="text"
                    autoCapitalize="none"
                    autoComplete="off"
                    spellCheck="false"
                    value={inputValue}
                    onBlur={onBlurInput}
                    onChange={onChangeInput}
                    onFocus={onFocusInput}
                    onKeyDown={onKeyDownInput}
                    disabled={disabled}
                    ref={inputRef}
                />
            </div>
            {value && !hideClearButton && (
                <button
                    className="searchDropdown_button clear"
                    type="button"
                    onClick={onClear}
                >
                    <FontAwesomeIcon icon="times" />
                </button>
            )}
            <button
                className="searchDropdown_button toggleOpen"
                type="button"
                onClick={onButtonClick}
                tabIndex={-1}
            >
                <FontAwesomeIcon icon="chevron-down" />
            </button>
        </div>

        <div
            className={cn('searchDropdown_dropdown', {
                open: isOpen,
                in: isIn,
            })}
            style={pop.styles.popper}
            ref={setPopEl}
            {...pop.attributes.popper}
        >
            {isOpen && (
                <div className="searchDropdown_dropdownInner" ref={scrollerRef}>
                    {searcherStatus === AsyncStatus.Success && results.length === 0 && (
                        <div className="searchDropdown_noResults">
                            No results found.
                        </div>
                    )}
                    {results.length > 0 && results.map((r, i) => (
                        <div
                            className={cn("searchDropdown_item", {
                                hover: hoverResult === r,
                                selected: value && getOptionValue(r) === selectedOptionValue,
                            })}
                            // NOTE: We can't use the option's value as a key because it might not be unique.
                            key={i}
                            onMouseEnter={() => {onEnterItem(r)}}
                            onClick={() => selectItem(r)}
                            ref={resultRefs[i]}
                        >
                                {renderOption(r)}
                        </div>
                    ))}
                    {(canLoadMore || searcherStatus === AsyncStatus.Loading) && (
                        <Loader />
                    )}
                    {searcherStatus === AsyncStatus.Error && (
                        <div className="searchDropdown_error">
                            We're sorry, an error occurred:
                            <div>{parseAxiosError(err)}</div>
                        </div>
                    )}
                </div>
            )}
        </div>
    </>);
}

// "forwardRef" doesn't support generic components, so we have to manually specify the type here.
// See notes on public API. We're using ref forwarding to expose a few methods that a parent component can call.
export const SearchDropdown = forwardRef<ISearchDropdownAPI, ISearchDropdownProps<any>>(_SearchDropdown) as
    <TOption>(p: PropsWithChildren<ISearchDropdownProps<TOption>> & {ref?: React.MutableRefObject<ISearchDropdownAPI>}) => ReactElement;